import { SortModel } from '@components/DataTable/types';
import TalentSearchFilter from '@modules/talent-search/TalentSearchFilter';
import { useTalentSearch } from '@modules/talent-search/hooks/useTalentSearch';
import { ITalentSearchFilter } from '@modules/talent-search/types';
import TalentTableOrganisms from '@modules/talents/TalentTableOrganisms';
import { Stack } from '@mui/material';
import { useCallback, useMemo } from 'react';

const TalentSearchModule = () => {
  const {
    filters,
    setFilters,
    updateFilter,
    sortBy,
    setSortBy,
    talents,
    totalDocs,
    loading,
    isLoadingMore,
    hasMore,
    loadMore,
    page,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    availableFilters,
  } = useTalentSearch({ context: 'GLOBAL' });

  const onQueryChange = useCallback(
    (value: string) => updateFilter('query', value),
    [updateFilter],
  );

  const onFilterChange = useCallback(
    (newFilter: ITalentSearchFilter) => setFilters(newFilter),
    [setFilters],
  );

  // Map talents from search results to the format TalentTableOrganisms expects
  const talentRows = useMemo(
    () => talents.map((item: any) => item.talent || item),
    [talents],
  );

  const sortModel = useMemo<SortModel[]>(() => {
    const fieldMap: Record<string, string> = {
      NAME: 'name',
      EXPERIENCE: 'experience',
      SALARY: 'salary',
      RATING: 'rating',
      CREATED_AT: 'createdAt',
      CITY: 'city',
      TOTAL_APPLICATIONS: 'totalApplications',
    };
    const columnId = fieldMap[sortBy.field];
    if (!columnId) return [];
    return [{ field: columnId, sort: sortBy.order === 'ASC' ? 'asc' : 'desc' }];
  }, [sortBy]);

  const handleTableSortChange = useCallback(
    (model: SortModel[]) => {
      console.log(model)
      const columnToField: Record<string, string> = {
        name: 'NAME',
        experience: 'EXPERIENCE',
        salary: 'SALARY',
        rating: 'RATING',
        createdAt: 'CREATED_AT',
        city: 'CITY',
        totalApplications: 'TOTAL_APPLICATIONS',
      };
      const active = model[0];
      if (!active?.sort) {
        setSortBy({ field: 'CREATED_AT', order: 'DESC' });
        return;
      }
      const field = columnToField[active.field];
      if (field) {
        setSortBy({ field, order: active.sort === 'asc' ? 'ASC' : 'DESC' });
      }
    },
    [setSortBy],
  );

  return (
    <Stack spacing={2} height="100%" flex={1}>
      <TalentSearchFilter
        context="GLOBAL"
        totalDocs={totalDocs}
        onQueryChange={onQueryChange}
        onFilterChange={onFilterChange}
        filter={filters}
        availableFilters={availableFilters}
      />
      <TalentTableOrganisms
        viewMode="list"
        data={talentRows}
        loading={loading}
        sortModel={sortModel}
        onSortChange={handleTableSortChange}
        isLoadingMore={isLoadingMore}
        onScrollEnd={hasMore ? loadMore : undefined}
        pagination={{
          currentPage: page + 1,
          pageSize,
          totalRows: totalDocs,
          onPageChange: handlePageChange,
          onPageSizeChange: handlePageSizeChange,
        }}
        handleDownload={() => {/* Implement download logic here, e.g., trigger a file download with current filters/sort */}}
      />
    </Stack>
  );
};

export default TalentSearchModule;
